import { Main } from "@/components/_common/main";
import { AiShareButton } from "@/components/ai-share-button";
import { MDXRenderer } from "@/components/mdx-renderer";
import ScrollBar from "@/components/scroll-bar";
import { TableOfContents } from "@/components/table-of-contents";
import { Badge } from "@/components/ui/badge";
import { formatDate, type BlogPost } from "@lib/blog";
import { getPostBySlugServer } from "@lib/blog.server";
import { createFileRoute, ErrorComponent, notFound } from "@tanstack/react-router";
import { Clock, Calendar } from "lucide-react";

import { seo } from "@lib/utils";

const PostPage = () => {
  const post = Route.useLoaderData() as BlogPost;

  if (!post) {
    return (
      <Main className="relative">
        <div className="container mx-auto px-4 py-8 text-center">
          <h1 className="text-2xl font-bold mb-4">Post not found</h1>
        </div>
      </Main>
    );
  }

  return (
    <>
      <Main className="relative mt-16">
        <ScrollBar />
        <div className="container mx-auto px-4 py-8 max-w-4xl">
          <article>
            {post.image && (
              <div className="aspect-video overflow-hidden rounded-lg mb-8">
                <img src={post.image} alt={post.title} className="h-full w-full object-cover" />
              </div>
            )}

            <header className="mb-8">
              <div className="flex flex-wrap justify-between items-center mb-4">
                <h1 id="title" className="text-2xl font-bold ">
                  {post.title}
                </h1>
                <AiShareButton content={post.content} title={post.title} />
              </div>

              <div className="flex flex-wrap items-center gap-4 text-muted-foreground mb-6">
                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4" />
                  <time>{formatDate(post.date)}</time>
                </div>

                <div className="flex items-center gap-2">
                  <Clock className="h-4 w-4" />
                  <span>{post.readingTime} min read</span>
                </div>

                {post.author && <a href="https://infosec.exchange/@ley0x">By {post.author}</a>}

                {post.lang && <Badge variant="outline">{post.lang.toUpperCase()}</Badge>}
              </div>

              {post.description && (
                <p className="text-lg text-muted-foreground mb-6">{post.description}</p>
              )}

              {post.tags.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {post.tags.map((tag) => (
                    <Badge
                      key={tag}
                      variant="outline"
                      className="py-2 px-4 rounded-full bg-primary/20 border border-primary/80 hover:bg-primary/35"
                    >
                      {tag}
                    </Badge>
                  ))}
                </div>
              )}
            </header>
            <MDXRenderer compiledMDX={post.compiledMDX} fallbackContent={post.content} />
          </article>
        </div>
      </Main>
      <TableOfContents />
    </>
  );
};

export const Route = createFileRoute("/post/$slug")({
  head: ({ loaderData: post }) => ({
    meta: [
      {
        charSet: "utf-8",
      },
      {
        name: "viewport",
        content: "width=device-width, initial-scale=1",
      },
      ...seo({
        title: `${(post as unknown as BlogPost)?.title || "Post"} | ley0x`,
        description:
          (post as unknown as BlogPost)?.description ||
          `ley0x's blog, where I write about programming, technology, linux, open source, and other things.`,
        image: (post as unknown as BlogPost)?.image,
      }),
    ],
    links: [
      { rel: "icon", type: "image/x-icon", href: "/favicon.ico" },
      { rel: "shortcut icon", type: "image/x-icon", href: "/favicon.ico" },
      { rel: "apple-touch-icon", href: "/favicon.ico" },
    ],
  }),
  errorComponent: ErrorComponent,
  component: PostPage,
  loader: async ({ params }): Promise<BlogPost> => {
    try {
      const post = await getPostBySlugServer({ data: params.slug });
      if (!post) {
        throw notFound();
      }
      return post;
    } catch (error) {
      console.error("Error loading post:", error);
      throw notFound();
    }
  },
});
